import type { WizardNavigationContextValue } from '@lemonade-hq/chat-wizard-client-base';
import { useCallback, useState } from 'react';
import { ScrollSnapItem } from './useWizardScrollSnapController.css';

export const EDIT_COMPLETION_DELAY_MS = 300;

export interface UseHistoryEdit {
  readonly onHistoryEdit: (stepName: string) => void;
  readonly editingEntryId: string | null;
}

interface UseHistoryEditProps {
  readonly steps: { readonly id: string }[];
  readonly currentStepIndex: number;
  readonly back: WizardNavigationContextValue['back'];
  readonly disableScrollSnap: (items: ScrollSnapItem[]) => void;
  readonly enableScrollSnap: (items: ScrollSnapItem[]) => void;
  readonly truncateObsoleteLocalSteps: (newCurrentIndexStep: number) => void;
  readonly openEditHistoryDialog: (stepName: string, callback: () => void) => void;
}

export const useWizardHistoryEdit = ({
  steps,
  back,
  currentStepIndex,
  disableScrollSnap,
  enableScrollSnap,
  truncateObsoleteLocalSteps,
  openEditHistoryDialog,
}: UseHistoryEditProps): UseHistoryEdit => {
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);

  const onHistoryEdit = useCallback(
    (stepId: string) => {
      const newStepIndex = steps.findIndex(step => step.id === stepId);
      if (newStepIndex === -1) return;

      const confirmEdit = (): void => {
        disableScrollSnap([ScrollSnapItem.Main, ScrollSnapItem.History]);
        setEditingEntryId(stepId);
        setTimeout(() => {
          const numberOfStepsBack = currentStepIndex - newStepIndex;
          const newCurrentIndexStep = back(numberOfStepsBack) as number;
          truncateObsoleteLocalSteps(newCurrentIndexStep);
          setEditingEntryId(null);
          enableScrollSnap([ScrollSnapItem.Main, ScrollSnapItem.History]);
        }, EDIT_COMPLETION_DELAY_MS);
      };

      if (newStepIndex < currentStepIndex - 1) {
        openEditHistoryDialog(stepId, confirmEdit);
      } else {
        confirmEdit();
      }
    },
    [
      steps,
      back,
      currentStepIndex,
      disableScrollSnap,
      enableScrollSnap,
      truncateObsoleteLocalSteps,
      openEditHistoryDialog,
    ],
  );

  return { onHistoryEdit, editingEntryId };
};
